import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatGBP } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { ChevronRight, Star, Minus, Plus, Truck, RotateCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [length, setLength] = useState(null);
  const [texture, setTexture] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, comment: "" });

  useEffect(() => {
    let alive = true;
    api.get(`/products/${slug}`).then((r) => {
      if (!alive) return;
      const p = r.data;
      setProduct(p);
      setActiveImg(0);
      setLength(p.lengths?.[0] || p.length || null);
      setTexture(p.textures?.[0] || p.texture || null);
      setColor(p.colors?.[0] || p.color || null);
      api.get("/reviews", { params: { product_id: p.product_id } }).then((rr) => alive && setReviews(rr.data));
    }).catch(() => alive && setProduct(false));
    return () => { alive = false; };
  }, [slug]);

  if (product === null) return <div className="py-32 text-center text-[#5C4A4D]">Loading...</div>;
  if (product === false) return <div className="py-32 text-center"><p className="font-serif-display text-3xl">Product not found.</p><Link to="/shop" className="btn-outline mt-6 inline-flex">Back to shop</Link></div>;

  const images = (product.images && product.images.length > 0) ? product.images : [product.image_url];
  const out = product.stock <= 0;
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  const onAdd = () => {
    if (out) return toast.error("Sorry, this crown is sold out.");
    add(product, { length, texture, color, quantity: qty });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.comment) return toast.error("Please add your name and review.");
    try {
      await api.post("/reviews", { ...reviewForm, product_id: product.product_id });
      const r = await api.get("/reviews", { params: { product_id: product.product_id } });
      setReviews(r.data);
      setReviewForm({ name: "", rating: 5, comment: "" });
      toast.success("Thank you for your review!");
    } catch {
      toast.error("Could not submit review.");
    }
  };

  return (
    <div className="bg-[#FFF4E0]" data-testid="product-detail-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-8">
        <nav className="text-xs uppercase tracking-[0.2em] text-[#5C4A4D] flex items-center gap-2">
          <Link to="/" className="hover:text-[#831132]">Home</Link>
          <ChevronRight size={12} />
          <Link to="/shop" className="hover:text-[#831132]">Shop</Link>
          <ChevronRight size={12} />
          <span className="text-[#1A0F11]">{product.name}</span>
        </nav>
      </div>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Gallery */}
        <div className="lg:col-span-7 grid grid-cols-12 gap-3">
          {images.length > 1 && (
            <div className="col-span-2 hidden md:flex flex-col gap-3">
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border ${activeImg === i ? "border-[#831132]" : "border-transparent"}`}
                  data-testid={`thumb-${i}`}
                  aria-label={`thumbnail ${i + 1}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className={`${images.length > 1 ? "col-span-12 md:col-span-10" : "col-span-12"} aspect-[4/5] bg-[#FDF1EC] overflow-hidden`}>
            <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover" data-testid="product-hero-image" />
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <div className="overline">{product.category.replace("-", " ")}</div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-[#1A0F11] mt-2 leading-tight">{product.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            {avgRating && (
              <div className="flex items-center gap-1 text-[#C5A059]">
                {[1,2,3,4,5].map(i => <Star key={i} size={14} fill={i <= Math.round(avgRating) ? "#C5A059" : "transparent"} stroke="#C5A059" />)}
                <span className="text-xs text-[#5C4A4D] ml-1">{avgRating} ({reviews.length})</span>
              </div>
            )}
            <span className={`text-xs uppercase tracking-[0.2em] ${out ? "text-[#831132]" : "text-[#2D4A22]"}`} data-testid="stock-status">
              {out ? "Sold out" : `In stock · ${product.stock} available`}
            </span>
          </div>
          <div className="mt-6 font-serif-display text-3xl text-[#1A0F11]" data-testid="product-price">{formatGBP(product.price)}</div>
          <p className="mt-6 text-[#5C4A4D] leading-relaxed">{product.description}</p>

          <div className="mt-8 space-y-6">
            {product.lengths?.length > 0 && (
              <div>
                <div className="label-luxe">Length</div>
                <div className="flex flex-wrap gap-2">
                  {product.lengths.map((l) => (
                    <button
                      key={l}
                      onClick={() => setLength(l)}
                      className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border ${length === l ? "bg-[#831132] text-[#FFF4E0] border-[#831132]" : "border-[#E8D8C8] text-[#1A0F11] hover:border-[#831132]"}`}
                      data-testid={`length-option-${l.replace(/\s/g, '')}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {product.textures?.length > 0 && (
              <div>
                <div className="label-luxe">Texture</div>
                <div className="flex flex-wrap gap-2">
                  {product.textures.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTexture(t)}
                      className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border ${texture === t ? "bg-[#831132] text-[#FFF4E0] border-[#831132]" : "border-[#E8D8C8] text-[#1A0F11] hover:border-[#831132]"}`}
                      data-testid={`texture-option-${t.replace(/\s/g, '')}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {product.colors?.length > 0 && (
              <div>
                <div className="label-luxe">Colour</div>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border ${color === c ? "bg-[#831132] text-[#FFF4E0] border-[#831132]" : "border-[#E8D8C8] text-[#1A0F11] hover:border-[#831132]"}`}
                      data-testid={`color-option-${c.replace(/\s/g, '')}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center border border-[#E8D8C8]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3 hover:bg-[#FDF1EC]" data-testid="qty-dec" aria-label="Decrease"><Minus size={14} /></button>
              <span className="px-4 text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-3 hover:bg-[#FDF1EC]" data-testid="qty-inc" aria-label="Increase"><Plus size={14} /></button>
            </div>
            <button onClick={onAdd} disabled={out} className="btn-primary flex-1" data-testid="add-to-cart-button">
              {out ? "Sold Out" : "Add to Bag"}
            </button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 text-xs text-[#5C4A4D]">
            <div className="flex flex-col items-center text-center gap-1">
              <Truck size={18} className="text-[#831132]" />
              <span>Free UK over £200</span>
            </div>
            <div className="flex flex-col items-center text-center gap-1">
              <RotateCw size={18} className="text-[#831132]" />
              <span>14-day returns</span>
            </div>
            <div className="flex flex-col items-center text-center gap-1">
              <ShieldCheck size={18} className="text-[#831132]" />
              <span>Secure checkout</span>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-16 border-t border-[#E8D8C8]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline">Voices of the Crown</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A0F11] mt-2">Reviews</h2>
            <p className="text-sm text-[#5C4A4D] mt-3">{reviews.length} verified review{reviews.length === 1 ? "" : "s"}</p>

            <form onSubmit={submitReview} className="mt-8 space-y-4 max-w-md" data-testid="review-form">
              <div>
                <div className="label-luxe">Your name</div>
                <input value={reviewForm.name} onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })} className="input-luxe" placeholder="Adaeze" data-testid="review-name" />
              </div>
              <div>
                <div className="label-luxe">Rating</div>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map((i) => (
                    <button type="button" key={i} onClick={() => setReviewForm({ ...reviewForm, rating: i })} data-testid={`review-star-${i}`} aria-label={`${i} stars`}>
                      <Star size={22} fill={i <= reviewForm.rating ? "#C5A059" : "transparent"} stroke="#C5A059" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-luxe">Your review</div>
                <textarea value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} rows={4} className="input-luxe resize-none" placeholder="The bundles arrived perfectly..." data-testid="review-comment" />
              </div>
              <button type="submit" className="btn-primary" data-testid="review-submit">Post review</button>
            </form>
          </div>
          <div className="md:col-span-7 space-y-8">
            {reviews.length === 0 && <p className="text-[#5C4A4D]">Be the first to share your experience.</p>}
            {reviews.map((r) => (
              <div key={r.review_id} className="border-b border-[#E8D8C8] pb-6" data-testid={`review-${r.review_id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex">
                    {[1,2,3,4,5].map((i) => <Star key={i} size={14} fill={i <= r.rating ? "#C5A059" : "transparent"} stroke="#C5A059" />)}
                  </div>
                  <span className="font-serif-accent italic text-[#1A0F11]">{r.name}</span>
                </div>
                <p className="mt-3 text-[#1A0F11] leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
